import { ChevronDown, X } from "lucide-react";
import { Button } from "./ui/button";
import coin from "../assets/images/placeholder/crypto-colour.png";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import PairSelectionContent from "./PairSelectionContent";

interface TradingPair {
  id: string;
  pair: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  type?: string;
  formattedAvailableAmount?: string;
  [key: string]: unknown;
}

interface Asset {
  assetId: string;
  asset: string;
  [key: string]: unknown;
}

interface PairSelectionDialogProps {
  selectedPair: TradingPair | null;
  selectedAsset: Asset | null | undefined;
  currentPairs: TradingPair[] | null | undefined;
  onPairSelect: (pair: TradingPair) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  emptyStateMessage?: string;
  loadingMessage?: string;
}

export default function PairSelectionDialog({
  selectedPair,
  selectedAsset,
  currentPairs,
  onPairSelect,
  open,
  onOpenChange,
  title = "Pay With",
  emptyStateMessage,
  loadingMessage,
}: PairSelectionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center">
              {selectedPair ? (
                <img
                  src={selectedPair.assetIcon}
                  alt={selectedPair.assetName ?? ""}
                  width={32}
                  height={32}
                  className="text-white"
                />
              ) : (
                <img
                  src={coin}
                  alt={"Default Asset"}
                  width={32}
                  height={32}
                  className="text-white"
                />
              )}
            </div>
            <div className="text-left">
              <div className="font-semibold">
                {selectedPair?.asset.toUpperCase()}
              </div>
              {!selectedPair?.assetName && (
                <div className="text-text-secondary">Select...</div>
              )}
            </div>
          </div>
          <ChevronDown className="text-text-secondary" />
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-sm rounded-2xl p-6">
        <DialogClose asChild>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-neutral hover:bg-gray-100"
          >
            <X size={18} className="text-gray-700" />
          </Button>
        </DialogClose>

        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-800">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <PairSelectionContent
            selectedAsset={selectedAsset}
            currentPairs={currentPairs}
            onPairSelect={onPairSelect}
            emptyStateMessage={emptyStateMessage}
            loadingMessage={loadingMessage}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
